/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { debounce } from "lodash";
import { toast } from "react-toastify";
import Button from "../../components/Button/Button";
import InputField from "../../components/InputField/InputField";
import {
  User,
  CreateUserInput,
  PermissionEnum,
  Marketplace,
} from "../../TypesAndInterfaces";
import { useLazyQuery, useMutation } from "@apollo/client";
import { getUsersQuery } from "./queries";
import { enableUserMutation } from "./mutations";
import CreateUserForm from "components/CreateUserForm/CreateUserForm";
import DisableUserForm from "components/DisableForm/DisableForm";
import DeleteUserForm from "components/DeleteUserForm/DeleteUserForm";
import usePermission from "hooks/usePermission";
import { displayError } from "utils/displayNotification";
import ToggleButton from "components/ToggleButton/ToggleButton";
import DataTable, { TableColumn } from "react-data-table-component";
import Checkbox from "components/Checkbox/Checkbox";
import Dropdown from "components/Dropdown/Dropdown";
import { getMarketplacesQuery } from "pages/Marketplaces/queries";

type MarketplacesOptions = {
  label: string;
  value: number;
};

interface IProps {
  _marketplaceId?: number;
}

const MarketplaceUsers = ({ _marketplaceId }: IProps) => {
  //#region hooks
  const [totalNumberOfUsers, setTotalNumberOfUsers] = useState(0);
  const [initialData, setInitialData] = useState<User[]>([]);
  const [data, setData] = useState(initialData);
  const [usersToDisplayPerPage, setUsersToDisplayPerPageDisplayPerPage] =
    useState<User[]>([]);
  const initialUserState: CreateUserInput = {
    email: "",
    disabled: false,
    name: "",
    password: "",
    permissions: [],
  };
  const [isDisableUserModalOpened, setIsDisableUserModalOpened] =
    useState(false);
  const [isCreateUserModalOpened, setIsCreateUserModalOpened] = useState(false);
  const [isDeleteUserModalOpened, setIsDeleteUserModalOpened] = useState(false);
  const [userId, setUserId] = useState<number>();
  const [userToDelete, setUserToDelete] = useState<User>();
  const [userToDisable, setUserToDisable] = useState<User>();
  const [index, setIndex] = useState(1);
  const [numberOfUsersPerPage, setNumberOfUsersPerPage] = useState(10);
  const [filterEnabledChecked, setFilterEnabledChecked] = useState(true);
  const [filterDisabledChecked, setFilterDisabledChecked] = useState(true);
  const [searchText, setSearchText] = useState("");
  const { isAuthorized: canManageUsers } = usePermission(PermissionEnum.ADMIN);
  const [filtersAreDisabled, setFiltersAreDisabled] = useState(false);
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
  const [marketplaces, setMarketplaces] = useState<MarketplacesOptions[]>([]);
  const [marketplaceId, setMarketplaceId] = useState(_marketplaceId);
  const [marketplaceName, setMarketplaceName] = useState("");
  //#endregion

  const marketplaceMapper = (
    marketplaces: Marketplace[]
  ): MarketplacesOptions[] =>
    marketplaces.map((marketplace) => ({
      value: marketplace.id,
      label: marketplace.name,
    }));

  //#region API CALLS
  const [getMarketplaces, { loading: marketplacesLoading }] = useLazyQuery<
    { getMarketplaces: { marketplaces: Marketplace[]; totalCount: number } },
    {
      pagination: { size: number; index: number };
      filter?: { enabled?: boolean; searchText?: string };
    }
  >(getMarketplacesQuery, {
    fetchPolicy: "no-cache",
    onCompleted: (dataMarketplaces) => {
      if (dataMarketplaces) {
        setFiltersAreDisabled(false);
        const { marketplaces } = dataMarketplaces?.getMarketplaces;
        setMarketplaces(marketplaceMapper(marketplaces));
      }
    },
    onError: (error) => {
      setFiltersAreDisabled(false);
      displayError(`Something went wrong ${error.message}`);
    },
  });

  const [getUsers, { loading }] = useLazyQuery<
    { getUsers: { users: User[]; totalCount: number } },
    {
      marketplaceId: number;
      pagination: { size: number; index: number };
      filter?: { enabled?: boolean; searchText?: string };
    }
  >(getUsersQuery, {
    fetchPolicy: "no-cache",
    onCompleted: (dataUsers) => {
      if (dataUsers) {
        setFiltersAreDisabled(false);
        const { users, totalCount } = dataUsers.getUsers;
        setUsersToDisplayPerPageDisplayPerPage(users);
        setInitialData(initialData.concat(users));
        setTotalNumberOfUsers(totalCount);
      }
    },
    onError: (error) => {
      setFiltersAreDisabled(false);
      displayError(`Something went wrong ${error.message}`);
    },
  });

  const [enableUser] = useMutation(enableUserMutation, {
    onCompleted: () => {
      toast.info(`User enabled.`, {
        position: "bottom-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    },
  });
  //#endregion

  const handleMarketplaceSelection = (marketplace: MarketplacesOptions) => {
    setMarketplaceName(marketplace.label);
    setMarketplaceId(marketplace.value);
  };

  const toggleStatus = async (row: User) => {
    if (row.disabled) {
      await enableUser({ variables: { userId: row.id } });
      fetchUsers();
    } else {
      setUserToDisable(row);
    }
  };

  const fetchUsers = async (searchValue?: string) => {
    if (marketplaceId) {
      setFiltersAreDisabled(true);
      let filter = {};
      const value = searchValue !== undefined ? searchValue : searchText;
      if (filterEnabledChecked && !filterDisabledChecked) {
        filter = { enabled: true, searchText: value };
      } else if (!filterEnabledChecked && filterDisabledChecked) {
        filter = { enabled: false, searchText: value };
      } else {
        filter = { searchText: value };
      }

      await getUsers({
        variables: {
          marketplaceId,
          pagination: { size: numberOfUsersPerPage, index },
          filter,
        },
      });
    }
  };

  const columnsUsers: TableColumn<User>[] = [
    {
      name: "ID",
      selector: (row) => row.id,
      sortable: false,
    },
    {
      name: "NAME",
      selector: (row) => row.name,
      sortable: false,
    },
    {
      name: "EMAIL",
      selector: (row) => row.email,
      sortable: false,
    },
    {
      name: "STATUS",
      selector: (row: User) => {
        return (
          <ToggleButton
            checked={!row.disabled}
            onChange={() => toggleStatus(row)}
            checkedText={"enabled"}
            uncheckedText={"disabled"}
          />
        );
      },
      sortable: false,
    } as any,
    {
      name: "ACTIONS",
      selector: (row: any) => {
        return (
          <div>
            <Button
              icon="pencil"
              type="button"
              className={"button--circle"}
              onClick={() => setUserId!(row.id)}
            />
            <Button
              icon="trash"
              type="button"
              className={"button--circle"}
              onClick={() => {
                setUserToDelete!(row);
              }}
            />
          </div>
        );
      },
    },
  ];

  const getColumnsUsers = () => {
    if (!canManageUsers) columnsUsers.length = columnsUsers.length - 2;
    return columnsUsers;
  };

  const filterData = () => {
    if (!filterEnabledChecked && !filterDisabledChecked) {
      setInitialData([]);
      setData([]);
    } else {
      fetchUsers();
    }
  };

  const handleSearch = debounce((searchValue: string) => {
    setSearchText(searchValue);
    fetchUsers(searchValue);
  }, 500);

  useEffect(() => {
    if (_marketplaceId) {
      fetchUsers();
    } else {
      fetchMarketplaces();
    }
  }, [marketplaceId]);

  useEffect(() => {
    setData(usersToDisplayPerPage);
  }, [usersToDisplayPerPage]);

  useEffect(() => {
    filterData();
  }, [filterEnabledChecked, filterDisabledChecked]);

  useEffect(() => {
    fetchUsers();
  }, [index, numberOfUsersPerPage, marketplaceId]);

  useEffect(() => {
    if (userId) {
      setIsCreateUserModalOpened(true);
    }
  }, [userId]);

  useEffect(() => {
    if (userToDisable) {
      setIsDisableUserModalOpened(true);
    }
  }, [userToDisable]);

  useEffect(() => {
    if (userToDelete) {
      setIsDeleteUserModalOpened(true);
    }
  }, [userToDelete]);

  const fetchMarketplaces = async () => {
    await getMarketplaces();
  };

  const changePage = (page: number, totalRows: any) => {
    setIndex(page);
  };

  const changeRowsPerPage = (
    currentRowsPerPage: number,
    currentPage: number
  ) => {
    setNumberOfUsersPerPage(currentRowsPerPage);
    setIndex(currentPage);
  };

  const resetFilters = () => {
    setFilterEnabledChecked(true);
    setFilterDisabledChecked(true);
    setIndex(0); // to update the table
    setIndex(1);
    setResetPaginationToggle(!resetPaginationToggle);
  };

  return (
    <div
      className={
        marketplaceId
          ? "content-wrapper"
          : "content-wrapper all-users-container"
      }
    >
      <DeleteUserForm
        isDeleteUserModalOpened={isDeleteUserModalOpened}
        userToDelete={userToDelete!}
        setIsDeleteUserModalOpened={setIsDeleteUserModalOpened}
        fetchUsers={fetchUsers}
      />
      <DisableUserForm
        dataType="User"
        isDisableModalOpened={isDisableUserModalOpened}
        dataToDisable={userToDisable!}
        setIsDisableModalOpened={setIsDisableUserModalOpened}
        fetchFunction={fetchUsers}
      />
      <CreateUserForm
        initialUserState={initialUserState}
        fetchUsers={fetchUsers}
        isCreateUserModalOpened={isCreateUserModalOpened}
        setIsCreateUserModalOpened={setIsCreateUserModalOpened}
        userId={userId}
        setUserId={setUserId}
        marketplaceId={marketplaceId}
      />
      <main className="main-container-wrapper">
        <section className="main-container">
          {!marketplacesLoading && !_marketplaceId && (
            <div className="marketplace-dropdown-container">
              <Dropdown
                options={marketplaces}
                placeholder="Search & Select a Marketplace"
                onChange={handleMarketplaceSelection}
                className="marketplaces-dropdown"
              />
            </div>
          )}
          {marketplaceId && (
            <header className="main-container__header">
              <h2 className="main-container__title">{marketplaceName} Users</h2>
              <div className="main-container__search-container">
                <InputField
                  type="search"
                  icon="search"
                  className="no-margin"
                  onChange={(e) => handleSearch(e.target.value)}
                  placeholder="Enter your search term"
                />
              </div>
            </header>
          )}
          {marketplaceId && (
            <div className="main-content">
              <aside className="main-content__sidebar">
                <header className="main-content__sidebar__header">
                  <h4 className="main-content__sidebar__title">Filters</h4>
                  <Button
                    className="button--small button--rounded button--no-wrap"
                    onClick={() => resetFilters()}
                    type="button"
                    content="RESET ALL"
                  ></Button>
                </header>
                <div className="filters">
                  <h5 className="filters__title">Status</h5>
                  <Checkbox
                    className="filters__checkbox"
                    label="Enabled"
                    checked={filterEnabledChecked}
                    onChange={() =>
                      setFilterEnabledChecked(!filterEnabledChecked)
                    }
                    disabled={filtersAreDisabled}
                  />
                  <Checkbox
                    className="filters__checkbox"
                    label="Disabled"
                    checked={filterDisabledChecked}
                    onChange={() =>
                      setFilterDisabledChecked(!filterDisabledChecked)
                    }
                    disabled={filtersAreDisabled}
                  />
                </div>
              </aside>
              <div className="main-content__result">
                {loading ? (
                  <div className="adyen-loader" />
                ) : (
                  <DataTable
                    pagination
                    paginationServer
                    paginationTotalRows={totalNumberOfUsers}
                    paginationPerPage={numberOfUsersPerPage}
                    paginationRowsPerPageOptions={[10, 25, 50, 100]}
                    onChangePage={changePage}
                    onChangeRowsPerPage={changeRowsPerPage}
                    columns={getColumnsUsers()}
                    data={data}
                    paginationDefaultPage={index}
                    paginationResetDefaultPage={resetPaginationToggle}
                  />
                )}
              </div>
            </div>
          )}
          <div className="bottom-button-container">
            {marketplaceId && canManageUsers && (
              <Button
                className={"button--icon-only button--add"}
                type="button"
                content={"+"}
                onClick={() => setIsCreateUserModalOpened(true)}
              />
            )}
          </div>
        </section>
      </main>
    </div>
  );
};

export default MarketplaceUsers;
